import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { usePageSEO } from '../../hooks/usePageSEO';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { createArticleStructuredData, createBreadcrumbStructuredData, createFAQStructuredData } from '../../utils/structuredData';

export default function SupportPage() {
  // SEO設定
  usePageSEO({
    title: 'DMM FXのサポート体制｜24時間対応で初心者も安心',
    description: 'DMM FXの充実したサポート体制を詳しく解説。24時間電話サポート、メール、LINE対応、HDI格付け三つ星の高品質サービス。初心者でも安心して取引できるサポート内容を紹介します。',
    keywords: 'DMM FX, サポート, 24時間, 電話, メール, LINE, HDI, カスタマーサポート',
    ogImage: 'https://public.readdy.ai/ai/img_res/96aededffee7f8f80ab6621e0f07bfcf.jpg',
    structuredData: [
      createArticleStructuredData({
        headline: 'DMM FXのサポート体制',
        description: 'DMM FXの24時間サポート体制を詳しく解説。電話、メール、LINEでの問い合わせ方法、対応内容、HDI格付け三つ星の高品質サービスを紹介します。',
        image: 'https://readdy.ai/api/search-image?query=DMM%20FX%20support&width=1200&height=630&seq=support-article&orientation=landscape',
        author: 'DMM FX情報局',
        datePublished: '2024-01-15',
        dateModified: new Date().toISOString().split('T')[0],
        url: '/support'
      }),
      createBreadcrumbStructuredData([
        { name: 'ホーム', url: '/' },
        { name: 'サポート', url: '/support' }
      ]),
      createFAQStructuredData([
        {
          question: 'DMM FXのサポート対応時間は何時から何時までですか？',
          answer: 'DMM FXの電話サポートとLINEサポートは、冬時間は月曜7:00〜土曜6:50、夏時間は月曜7:00〜土曜5:50の24時間対応です。メールサポートは24時間受付で、通常1営業日以内に返信いたします。平日であればいつでもサポートを受けられます。'
        },
        {
          question: 'どのような方法で問い合わせできますか？',
          answer: 'DMM FXでは、電話、メール、LINE、よくある質問（FAQ）の4つの方法で問い合わせができます。電話は緊急時にすぐ相談でき、メールは時間を気にせず詳細な質問ができ、LINEは気軽に質問できます。FAQは24時間いつでも閲覧可能で、よくある質問の答えをすぐに見つけられます。'
        },
        {
          question: 'どのようなサポートを受けられますか？',
          answer: 'DMM FXでは、口座開設サポート（申込方法、必要書類、審査状況確認）、取引サポート（注文方法、取引ツールの使い方、チャートの見方）、入出金サポート（クイック入金、出金手続き）、システムサポート（ログイン問題、パスワード再設定、アプリの不具合対応）、各種手続きサポート（登録情報変更、確定申告）、学習サポート（FXの基礎知識、取引戦略のアドバイス）など、幅広いサポートを提供しています。'
        },
        {
          question: '初心者でもサポートを受けられますか？',
          answer: 'はい、初心者の方でも安心してサポートを受けられます。DMM FXでは、FXの基礎知識から取引方法まで、専門スタッフが丁寧に説明します。また、動画マニュアル、オンラインセミナー、マーケット情報など、初心者向けの学習コンテンツも充実しています。分からないことがあれば、遠慮なくお問い合わせください。'
        },
        {
          question: 'サポートの品質はどうですか？',
          answer: 'DMM FXのカスタマーサポートは、HDI格付けで三つ星を獲得しており、高品質なサービスを提供しています。FXの知識豊富な専門スタッフが、24時間体制で対応しているため、初心者の方でも安心して取引を始められます。電話、メール、LINEなど、お好みの方法で気軽にお問い合わせいただけます。'
        }
      ])
    ]
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Header />
      
      {/* パンくずリスト */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 py-3">
          <Breadcrumb items={[
            { name: 'ホーム', url: '/' },
            { name: 'DMM FX取引ガイド', url: '/fx-trade' },
            { name: 'サポート体制', url: '/support' }
          ]} />
        </div>
      </div>
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-600 via-emerald-600 to-teal-700 text-white py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">DMM FXのサポート体制</h1>
            <p className="text-xl text-teal-50 mb-8 max-w-3xl mx-auto">
              24時間対応の充実したサポートで初心者でも安心
            </p>
            <a 
              href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
              rel="nofollow"
              className="inline-block bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              DMM FXで始める
            </a>
            <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
          </div>
        </div>
      </section>

      {/* サポート体制概要 Section */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">充実のサポート体制</h2>
            <p className="text-gray-700 text-lg leading-relaxed text-center mb-12 max-w-3xl mx-auto">
              DMM FXでは、初心者の方でも安心して取引できるよう、<span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded">24時間体制</span>のサポートを提供しています。電話、メール、LINEなど、お好みの方法でお問い合わせいただけます。
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-24-hours-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2"><span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded">24時間対応</span></h3>
                <p className="text-gray-600">
                  平日は24時間いつでもサポート対応
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-customer-service-2-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">専門スタッフ</h3>
                <p className="text-gray-600">
                  FXの知識豊富なスタッフが対応
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-question-answer-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">多様な問い合わせ方法</h3>
                <p className="text-gray-600">
                  電話・メール・LINEで対応
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* お問い合わせ方法 Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">お問い合わせ方法</h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-start">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mr-6 flex-shrink-0">
                  <i className="ri-phone-line text-2xl text-white"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">電話サポート</h3>
                  <div className="bg-teal-50 rounded-lg p-6 mb-4">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <p className="font-semibold text-teal-900 mb-2">受付時間</p>
                        <p className="text-gray-700 mb-4">
                          <span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded">月曜7:00 〜 土曜6:50</span><br />
                          ※米国夏時間は月曜7:00〜土曜5:50
                        </p>
                      </div>
                      <div>
                        <p className="font-semibold text-teal-900 mb-2">対応内容</p>
                        <p className="text-gray-700">
                          取引方法、システム操作、口座開設など全般
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm text-gray-700">
                    <p>✓ 緊急時にすぐ相談できる</p>
                    <p>✓ 複雑な質問も丁寧に対応</p>
                    <p>✓ 取引中のトラブルにも即対応</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-start">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mr-6 flex-shrink-0">
                  <i className="ri-mail-line text-2xl text-white"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">メールサポート</h3>
                  <div className="bg-teal-50 rounded-lg p-6 mb-4">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <p className="font-semibold text-teal-900 mb-2">受付時間</p>
                        <p className="text-gray-700 mb-4">
                          <span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded">24時間受付</span><br />
                          ※返信は営業時間内
                        </p>
                      </div>
                      <div>
                        <p className="font-semibold text-teal-900 mb-2">返信時間</p>
                        <p className="text-gray-700">
                          通常1営業日以内に返信
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm text-gray-700">
                    <p>✓ 時間を気にせず問い合わせ可能</p>
                    <p>✓ 詳細な質問も文章で伝えやすい</p>
                    <p>✓ 記録として残るので安心</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-start">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mr-6 flex-shrink-0">
                  <i className="ri-message-3-line text-2xl text-white"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">LINEサポート</h3>
                  <div className="bg-teal-50 rounded-lg p-6 mb-4">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <p className="font-semibold text-teal-900 mb-2">受付時間</p>
                        <p className="text-gray-700 mb-4">
                          <span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded">月曜7:00 〜 土曜6:50</span><br />
                          ※米国夏時間は月曜7:00〜土曜5:50
                        </p>
                      </div>
                      <div>
                        <p className="font-semibold text-teal-900 mb-2">対応内容</p>
                        <p className="text-gray-700">
                          簡単な質問や確認事項など
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm text-gray-700">
                    <p>✓ 気軽に質問できる</p>
                    <p>✓ スマホから簡単にアクセス</p>
                    <p>✓ チャット形式で会話しやすい</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-start">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mr-6 flex-shrink-0">
                  <i className="ri-question-line text-2xl text-white"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">よくある質問（FAQ）</h3>
                  <div className="bg-teal-50 rounded-lg p-6 mb-4">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <p className="font-semibold text-teal-900 mb-2">利用時間</p>
                        <p className="text-gray-700 mb-4">
                          24時間いつでも閲覧可能
                        </p>
                      </div>
                      <div>
                        <p className="font-semibold text-teal-900 mb-2">掲載内容</p>
                        <p className="text-gray-700">
                          口座開設、取引方法、システムなど
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm text-gray-700">
                    <p>✓ すぐに答えが見つかる</p>
                    <p>✓ 問い合わせ前に確認できる</p>
                    <p>✓ 豊富な情報量</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* サポート内容 Section */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">サポート対応内容</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-md p-8">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-user-add-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">口座開設サポート</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>申込方法の案内</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>必要書類の説明</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>審査状況の確認</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>初期設定のサポート</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-md p-8">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-exchange-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">取引サポート</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>注文方法の説明</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>取引ツールの使い方</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>チャートの見方</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>取引履歴の確認方法</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-md p-8">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-bank-card-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">入出金サポート</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>クイック入金の方法</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>出金手続きの案内</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>入出金履歴の確認</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>手数料に関する質問</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-md p-8">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-settings-3-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">システムサポート</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>ログインできない場合</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>パスワード再設定</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>アプリの不具合対応</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>システムメンテナンス情報</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-md p-8">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-file-text-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">各種手続きサポート</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>登録情報の変更</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>口座解約の手続き</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>確定申告に関する質問</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>各種書類の発行</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-md p-8">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-book-open-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">学習サポート</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>FXの基礎知識</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>取引戦略のアドバイス</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>セミナー情報の案内</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>マーケット情報の提供</span>
                </li>
              </ul>
            </div>
          </div>

          {/* 利用者の声へのリンク */}
          <div className="mt-12 bg-white rounded-xl p-6 border-l-4 border-teal-500 shadow-md">
            <p className="text-gray-700 leading-relaxed">
              実際の利用者の声や評判については、こちらの記事で詳しくまとめています。<br />
              → <Link to="/reviews" className="text-teal-600 font-semibold hover:text-teal-700 underline cursor-pointer">「DMM FXの口コミ・評判は？実際どうなのか調査」</Link>
            </p>
          </div>
        </div>
      </section>

      {/* その他のサポート Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">その他の充実サポート</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-md p-8 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-video-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">動画マニュアル</h3>
              <p className="text-gray-600 leading-relaxed">
                取引方法やツールの使い方を動画で分かりやすく解説
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-8 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-presentation-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">オンラインセミナー</h3>
              <p className="text-gray-600 leading-relaxed">
                初心者向けから上級者向けまで、定期的にセミナーを開催
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-8 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-newspaper-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">マーケット情報</h3>
              <p className="text-gray-600 leading-relaxed">
                最新の為替ニュースや経済指標をリアルタイムで配信
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-2xl shadow-xl p-12 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">充実のサポート体制で安心取引</h2>
            <p className="text-xl text-teal-50 mb-8">
              24時間対応のサポートで初心者でも安心してスタート
            </p>

            {/* 口座開設手順へのリンク */}
            <div className="mb-8 bg-white/10 backdrop-blur-sm rounded-xl p-6 max-w-2xl mx-auto">
              <p className="text-white leading-relaxed">
                口座開設の具体的な手順は、こちらで画像付きで解説しています。<br />
                → <Link to="/account-opening" className="text-teal-200 font-semibold hover:text-teal-100 underline cursor-pointer">「DMM FXの口座開設手順を画像付きで解説」</Link>
              </p>
            </div>

            <a 
              href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
              rel="nofollow"
              className="inline-block bg-white text-teal-600 px-10 py-4 rounded-lg text-lg font-bold hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              無料で口座開設する
            </a>
            <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
          </div>
        </div>
      </section>

      {/* Related Articles Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            サポートについてさらに詳しく知りたい方へ
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Link 
              to="/beginner-guide" 
              className="group bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-6 border-2 border-teal-100 hover:border-teal-300 hover:shadow-lg transition-all cursor-pointer"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-user-star-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                    初心者でも大丈夫？
                  </h3>
                  <p className="text-sm text-gray-600">
                    サポートを活用した初心者向けの始め方ガイド
                  </p>
                </div>
              </div>
            </Link>

            <Link 
              to="/safety" 
              className="group bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 border-2 border-emerald-100 hover:border-emerald-300 hover:shadow-lg transition-all cursor-pointer"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-shield-check-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">
                    危険？安全性を解説
                  </h3>
                  <p className="text-sm text-gray-600">
                    DMM FXの信頼性とセキュリティ体制を確認
                  </p>
                </div>
              </div>
            </Link>

            <Link 
              to="/app-guide" 
              className="group bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border-2 border-amber-100 hover:border-amber-300 hover:shadow-lg transition-all cursor-pointer"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-smartphone-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2 group-hover:text-amber-600 transition-colors">
                    アプリの使い方
                  </h3>
                  <p className="text-sm text-gray-600">
                    スマホアプリでのサポート機能と使い方
                  </p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
